import { type ReactElement, useCallback, useEffect, useState } from 'react';
import {
  PARTNER_BILLING_MODELS,
  type ApiError,
  type PartnerAccountView,
  type PartnerBillingModel,
  type PartnerCostCenterView,
  type PartnerCostControlView,
  type PartnerMonthlyLimit,
  type PartnerQuickZoneAdminView,
  type PartnerQuickZoneProposalView,
  type PartnerSpendLimitLevel,
} from '@vello/api-client';
import { isIso4217Currency } from '@vello/core';
import { formatMoney } from '@vello/format';
import { Badge, Button, CheckRow, EmptyState, Field, MoneyInput, Notice, Select, Table, TextInput, FormRow, FormGrid, SectionTitle, Text, Stack, Row, Divided } from '@vello/web-theme';
import { maySelfApprove } from '../../src/index';
import { useAdmin } from '../session';
import { useConsole } from '../console';
import { useT } from '../i18n/provider';
import { useVocab } from '../i18n/vocab';

type Explain = (e: ApiError) => string;

export function PartnerCostControlPanel({ account, mayManage, mayProposeFare, mayApproveFare, meId, explain }: { account: PartnerAccountView; mayManage: boolean; mayProposeFare: boolean; mayApproveFare: boolean; meId: string | undefined; explain: Explain }): ReactElement {
  const { runtime, grants } = useAdmin();
  const selfApproval = maySelfApprove(grants); // D868: a GLOBAL super administrator decides their own proposals
  const { t, lang } = useT();
  const { v } = useVocab();
  const { toast } = useConsole();
  const [view, setView] = useState<PartnerCostControlView | undefined>(undefined);
  const [failed, setFailed] = useState<string | undefined>(undefined);
  const currency = account.spendLimit?.currency;

  const load = useCallback(async () => {
    const res = await runtime.partnerCostControl.view(account.accountId);
    if (res.ok) {
      setView(res.value);
      setFailed(undefined);
    } else {
      setFailed(explain(res.error));
    }
  }, [runtime, account.accountId, explain]);
  useEffect(() => {
    void load();
  }, [load]);

  /** Run a write, then reload: a refusal is explained, a success is toasted. */
  const run = async <T,>(call: Promise<{ ok: true; value: T } | { ok: false; error: ApiError }>, doneKey: string): Promise<boolean> => {
    const res = await call;
    if (!res.ok) {
      toast({ message: explain(res.error), tone: 'warning' });
      return false;
    }
    toast({ message: t(doneKey), tone: 'success' });
    await load();
    return true;
  };

  if (failed !== undefined) return <Notice tone="warning">{failed}</Notice>;
  if (view === undefined) return <Text as="p" role="caption" tone="secondary">{t('pcc.loading')}</Text>;
  const money = (minor: string, cur: string): string => (/^\d+$/.test(minor) && isIso4217Currency(cur) ? formatMoney({ amountMinor: BigInt(minor), currency: cur }, { locale: lang, timeZone: 'UTC', measurement: 'metric' }) : '—');
  const limitText = (l: PartnerMonthlyLimit | null): string => {
    if (l === null) return t('pcc.limit.none');
    const parts: string[] = [];
    if (l.maxCount !== null) parts.push(t('pcc.limit.orders', { count: String(l.maxCount) }));
    if (l.max !== null) parts.push(money(l.max.amountMinor, l.max.currency));
    return parts.join(' · ');
  };

  return (
    <Stack gap={5}>
      <Text as="p" role="caption" tone="secondary">{t('pcc.intro', { start: view.month.start.slice(0, 10), end: view.month.end.slice(0, 10), zone: view.month.timezone })}</Text>

      <Stack gap={3}>
        <SectionTitle level={4} size="sub">{t('pcc.billing.title')}</SectionTitle>
        <FormRow>
          <Field label={t('pcc.billing.model')} htmlFor="pcc-billing">
            <Select id="pcc-billing" value={view.billingModel} disabled={!mayManage} onChange={(e) => void run(runtime.partnerCostControl.setBillingModel(account.accountId, e.target.value as PartnerBillingModel), 'pcc.saved')}>
              {PARTNER_BILLING_MODELS.map((m) => <option key={m} value={m}>{v('business.billing_model', m)}</option>)}
            </Select>
          </Field>
        </FormRow>
        <Text as="p" role="caption" tone="secondary">{t(`pcc.billing.${view.billingModel}.hint`)}</Text>
      </Stack>

      <Stack gap={3}>
        <SectionTitle level={4} size="sub">{t('pcc.account.title')}</SectionTitle>
        <Text as="p" role="caption" tone="secondary">{t('pcc.account.hint')}</Text>
        <LimitEditor level="ACCOUNT" current={view.accountLimit} currency={currency} mayManage={mayManage} summary={limitText(view.accountLimit)} onSave={(input) => run(runtime.partnerCostControl.setSpendLimit(account.accountId, { level: 'ACCOUNT', ...input }), 'pcc.saved')} onClear={() => run(runtime.partnerCostControl.clearSpendLimit(account.accountId, { level: 'ACCOUNT' }), 'pcc.cleared')} />
      </Stack>

      <Stack gap={3}>
        <SectionTitle level={4} size="sub">{t('pcc.centers.title')}</SectionTitle>
        <Table
          columns={[
            { key: 'code', header: t('pcc.col.code'), render: (c: PartnerCostCenterView) => <span className="tnum">{c.code}</span> },
            { key: 'name', header: t('pcc.col.name'), render: (c: PartnerCostCenterView) => c.name },
            { key: 'status', header: t('pob.col.status'), render: (c: PartnerCostCenterView) => <Badge tone={c.status === 'ACTIVE' ? 'success' : 'neutral'}>{v('billing.cost_center_status', c.status)}</Badge> },
            { key: 'month', header: t('pcc.col.month'), render: (c: PartnerCostCenterView) => (c.usage.length === 0 ? '—' : c.usage.map((u) => `${u.orderCount} · ${money(u.total.amountMinor, u.total.currency)}`).join(' / ')) },
            { key: 'limit', header: t('pcc.col.limit'), render: (c: PartnerCostCenterView) => limitText(c.limit) },
            {
              key: 'act', header: '',
              render: (c: PartnerCostCenterView) => mayManage ? (
                <Button variant="ghost" onClick={() => void run(runtime.partnerCostControl.updateCostCenter(account.accountId, c.costCenterId, { status: c.status === 'ACTIVE' ? 'ARCHIVED' : 'ACTIVE' }), 'pcc.saved')}>{t(c.status === 'ACTIVE' ? 'pcc.archive' : 'pcc.reactivate')}</Button>
              ) : null,
            },
          ]}
          rows={view.costCenters}
          rowKey={(c) => c.costCenterId}
          empty={<Text as="p" role="caption" tone="secondary" className="vello-admin-a-pad">{t('pcc.centers.empty')}</Text>}
        />
        {view.unassigned.length > 0 && <Text as="p" role="caption" tone="secondary">{t('pcc.unassigned', { total: view.unassigned.map((u) => `${u.orderCount} · ${money(u.total.amountMinor, u.total.currency)}`).join(' / ') })}</Text>}
        {mayManage && <NewCenter onCreate={(code, name) => run(runtime.partnerCostControl.createCostCenter(account.accountId, { code, name }), 'pcc.saved')} />}
        {mayManage && view.costCenters.length > 0 && <CenterLimit centers={view.costCenters.filter((c) => c.status === 'ACTIVE')} currency={currency} onSave={(id, input) => run(runtime.partnerCostControl.setSpendLimit(account.accountId, { level: 'COST_CENTER', scopeId: id, ...input }), 'pcc.saved')} onClear={(id) => run(runtime.partnerCostControl.clearSpendLimit(account.accountId, { level: 'COST_CENTER', scopeId: id }), 'pcc.cleared')} />}
      </Stack>

      <Stack gap={3}>
        <SectionTitle level={4} size="sub">{t('pcc.members.title')}</SectionTitle>
        <Text as="p" role="caption" tone="secondary">{t('pcc.members.hint')}</Text>
        {view.members.length === 0 ? <EmptyState title={t('pob.member.empty')} /> : view.members.map((m) => (
          <MemberRow key={m.membershipId} label={m.identifier ?? m.userId} limitText={limitText(m.limit)} centers={view.costCenters.filter((c) => c.status === 'ACTIVE')} selected={m.costCenterIds} currency={currency} limit={m.limit} mayManage={mayManage}
            onLink={(ids) => run(runtime.partnerCostControl.setMemberCostCenters(account.accountId, m.membershipId, ids), 'pcc.saved')}
            onSave={(input) => run(runtime.partnerCostControl.setSpendLimit(account.accountId, { level: 'MEMBERSHIP', scopeId: m.membershipId, ...input }), 'pcc.saved')}
            onClear={() => run(runtime.partnerCostControl.clearSpendLimit(account.accountId, { level: 'MEMBERSHIP', scopeId: m.membershipId }), 'pcc.cleared')} />
        ))}
      </Stack>

      <Stack gap={3}>
        <SectionTitle level={4} size="sub">{t('pcc.categories.title')}</SectionTitle>
        <Text as="p" role="caption" tone="secondary">{t('pcc.categories.hint')}</Text>
        {view.categories.map((c) => (
          <CategoriesRow key={c.marketId} marketId={c.marketId} codes={c.categoryCodes} mayManage={mayManage} onSave={(codes) => run(runtime.partnerCostControl.setCategories(account.accountId, c.marketId, codes), 'pcc.saved')} />
        ))}
      </Stack>

      <Stack gap={3}>
        <SectionTitle level={4} size="sub">{t('pcc.quick.title')}</SectionTitle>
        <Text as="p" role="caption" tone="secondary">{t('pcc.quick.hint')}</Text>
        <Table
          columns={[
            { key: 'label', header: t('pcc.col.name'), render: (z: PartnerQuickZoneAdminView) => z.label },
            { key: 'market', header: t('pob.field.market'), render: (z: PartnerQuickZoneAdminView) => z.marketId },
            { key: 'fare', header: t('pcc.quick.fare'), render: (z: PartnerQuickZoneAdminView) => (z.current !== null ? <span className="tnum">{money(z.current.fare.amountMinor, z.current.fare.currency)} · v{z.current.version}</span> : '—') },
            { key: 'status', header: t('pob.col.status'), render: (z: PartnerQuickZoneAdminView) => <Badge tone={z.status === 'ACTIVE' ? 'success' : 'neutral'}>{v('billing.cost_center_status', z.status)}</Badge> },
            {
              key: 'act', header: '',
              render: (z: PartnerQuickZoneAdminView) => mayManage || mayProposeFare ? (
                <Row gap={2} align="stretch" justify="end">
                  {mayProposeFare && <NewFare currency={z.current?.fare.currency ?? currency} onSave={(minor) => run(runtime.partnerCostControl.addQuickZoneVersion(account.accountId, z.zoneId, { fixedMinor: minor }), 'pcc.proposed')} />}
                  {mayManage && <Button variant="ghost" onClick={() => void run(runtime.partnerCostControl.setQuickZoneStatus(account.accountId, z.zoneId, z.status === 'ACTIVE' ? 'ARCHIVED' : 'ACTIVE'), 'pcc.saved')}>{t(z.status === 'ACTIVE' ? 'pcc.archive' : 'pcc.reactivate')}</Button>}
                </Row>
              ) : null,
            },
          ]}
          rows={view.quickZones}
          rowKey={(z) => z.zoneId}
          empty={<Text as="p" role="caption" tone="secondary" className="vello-admin-a-pad">{t('pcc.quick.empty')}</Text>}
        />
        {mayProposeFare && <NewZone markets={account.markets.map((m) => m.marketId)} currency={currency} onCreate={(input) => run(runtime.partnerCostControl.createQuickZone(account.accountId, input), 'pcc.proposed')} />}
      </Stack>

      <Stack gap={3}>
        <SectionTitle level={4} size="sub">{t('pcc.proposals.title')}</SectionTitle>
        <Text as="p" role="caption" tone="secondary">{t('pcc.proposals.hint')}</Text>
        <Table
          columns={[
            { key: 'kind', header: t('pcc.col.name'), render: (p: PartnerQuickZoneProposalView) => `${p.label} · ${v('partner.quick_zone_proposal_kind', p.kind)}` },
            { key: 'fare', header: t('pcc.quick.fare'), render: (p: PartnerQuickZoneProposalView) => <span className="tnum">{money(p.fare.amountMinor, p.fare.currency)}</span> },
            { key: 'floor', header: t('pcc.proposals.floor'), render: (p: PartnerQuickZoneProposalView) => <span className="tnum">{money(p.floor.amountMinor, p.floor.currency)}</span> },
            { key: 'status', header: t('pob.col.status'), render: (p: PartnerQuickZoneProposalView) => <Badge tone={p.status === 'APPROVED' ? 'success' : p.status === 'PENDING' ? 'neutral' : 'warning'}>{v('partner.quick_zone_proposal_status', p.status)}</Badge> },
            {
              key: 'act', header: '',
              render: (p: PartnerQuickZoneProposalView) => {
                if (p.status !== 'PENDING' || !mayApproveFare) return null;
                if (!selfApproval && meId !== undefined && meId === p.proposedBy) return <Text role="caption" tone="secondary">{t('pcc.proposals.own')}</Text>;
                return (
                  <Row gap={2} align="stretch" justify="end">
                    <Button variant="secondary" onClick={() => void run(runtime.partnerCostControl.decideQuickZoneProposal(p.proposalId, 'approve'), 'pcc.decided')}>{t('pcc.proposals.approve')}</Button>
                    <Button variant="ghost" onClick={() => void run(runtime.partnerCostControl.decideQuickZoneProposal(p.proposalId, 'reject'), 'pcc.decided')}>{t('pcc.proposals.reject')}</Button>
                  </Row>
                );
              },
            },
          ]}
          rows={view.quickZoneProposals}
          rowKey={(p) => p.proposalId}
          empty={<Text as="p" role="caption" tone="secondary" className="vello-admin-a-pad">{t('pcc.proposals.empty')}</Text>}
        />
      </Stack>
    </Stack>
  );
}

/** A count and/or a value (the account's own currency), saved or cleared. */
function LimitEditor({ level, current, currency, mayManage, summary, onSave, onClear }: { level: PartnerSpendLimitLevel; current: PartnerMonthlyLimit | null; currency: string | undefined; mayManage: boolean; summary: string; onSave: (input: { maxCount?: number; maxMinor?: string; currency?: string }) => Promise<boolean>; onClear: () => Promise<boolean> }): ReactElement {
  const { t, lang } = useT();
  const { v } = useVocab();
  const [count, setCount] = useState(current?.maxCount != null ? String(current.maxCount) : '');
  const [value, setValue] = useState<bigint | undefined>(current?.max != null ? BigInt(current.max.amountMinor) : undefined);
  const parsed = count.trim() === '' ? undefined : Number(count);
  const countOk = parsed === undefined || (Number.isInteger(parsed) && parsed >= 0);
  const empty = parsed === undefined && value === undefined;
  return (
    <div>
      <Text role="caption" tone="secondary">{summary}</Text>
      {mayManage && (
        <FormRow
          action={
            <>
          <Button variant="secondary" disabled={empty || !countOk} onClick={() => void onSave({ ...(parsed !== undefined ? { maxCount: parsed } : {}), ...(value !== undefined && currency !== undefined ? { maxMinor: value.toString(), currency } : {}) })}>{t('pcc.limit.save')}</Button>
          {current !== null && <Button variant="ghost" onClick={() => void onClear().then((ok) => { if (ok) { setCount(''); setValue(undefined); } })}>{t('pcc.limit.clear')}</Button>}
            </>
          }
        >
        <Field label={t('pcc.limit.countField')} htmlFor={`pcc-count-${level}`}>
          <TextInput id={`pcc-count-${level}`} className="tnum" inputMode="numeric" value={count} aria-invalid={!countOk} onChange={(e) => setCount(e.target.value)} />
        </Field>
        {currency !== undefined && <MoneyInput id={`pcc-value-${level}`} label={t('pcc.limit.valueField')} currency={currency} locale={lang} valueMinor={value} onChangeMinor={setValue} issueText={(issue) => v('format.money_input_issue', issue)} />}
        </FormRow>
      )}
    </div>
  );
}

function NewCenter({ onCreate }: { onCreate: (code: string, name: string) => Promise<boolean> }): ReactElement {
  const { t } = useT();
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  return (
    <FormRow
      action={
        <Button variant="secondary" disabled={code.trim() === '' || name.trim() === ''} onClick={() => void onCreate(code, name).then((ok) => { if (ok) { setCode(''); setName(''); } })}>{t('pcc.centers.new')}</Button>
      }
    >
      <Field label={t('pcc.col.code')} htmlFor="pcc-new-code"><TextInput id="pcc-new-code" value={code} maxLength={32} onChange={(e) => setCode(e.target.value)} /></Field>
      <Field label={t('pcc.col.name')} htmlFor="pcc-new-name"><TextInput id="pcc-new-name" value={name} maxLength={80} onChange={(e) => setName(e.target.value)} /></Field>
    </FormRow>
  );
}

/** Pick one active center and set or clear its monthly limit. */
function CenterLimit({ centers, currency, onSave, onClear }: { centers: readonly PartnerCostCenterView[]; currency: string | undefined; onSave: (id: string, input: { maxCount?: number; maxMinor?: string; currency?: string }) => Promise<boolean>; onClear: (id: string) => Promise<boolean> }): ReactElement | null {
  const { t } = useT();
  const [id, setId] = useState(centers[0]?.costCenterId ?? '');
  const selected = centers.find((c) => c.costCenterId === id) ?? centers[0];
  if (selected === undefined) return null;
  return (
    <div>
      <Field label={t('pcc.centers.limitFor')} htmlFor="pcc-center-pick">
        <Select id="pcc-center-pick" value={selected.costCenterId} onChange={(e) => setId(e.target.value)}>
          {centers.map((c) => <option key={c.costCenterId} value={c.costCenterId}>{`${c.code} · ${c.name}`}</option>)}
        </Select>
      </Field>
      <LimitEditor key={selected.costCenterId} level="COST_CENTER" current={selected.limit} currency={currency} mayManage summary="" onSave={(input) => onSave(selected.costCenterId, input)} onClear={() => onClear(selected.costCenterId)} />
    </div>
  );
}

function MemberRow({ label, limitText, centers, selected, currency, limit, mayManage, onLink, onSave, onClear }: { label: string; limitText: string; centers: readonly PartnerCostCenterView[]; selected: readonly string[]; currency: string | undefined; limit: PartnerMonthlyLimit | null; mayManage: boolean; onLink: (ids: string[]) => Promise<boolean>; onSave: (input: { maxCount?: number; maxMinor?: string; currency?: string }) => Promise<boolean>; onClear: () => Promise<boolean> }): ReactElement {
  const { t } = useT();
  const [open, setOpen] = useState(false);
  return (
    <Divided gap={3}>
      <Row gap={3} align="center" wrap>
        <strong>{label}</strong>
        <Text role="caption" tone="secondary">{selected.length === 0 ? t('pcc.members.allCenters') : t('pcc.members.linked', { count: String(selected.length) })}</Text>
        <Text role="caption" tone="secondary">{limitText}</Text>
        {mayManage && <Button variant="ghost" onClick={() => setOpen(!open)}>{t(open ? 'pcc.members.close' : 'pcc.members.edit')}</Button>}
      </Row>
      {open && mayManage && (
        <div>
          {centers.map((c) => (
            <CheckRow key={c.costCenterId} id={`pcc-link-${label}-${c.costCenterId}`} checked={selected.includes(c.costCenterId)} onChange={(on) => void onLink(on ? [...selected, c.costCenterId] : selected.filter((x) => x !== c.costCenterId))}>{`${c.code} · ${c.name}`}</CheckRow>
          ))}
          <LimitEditor level="MEMBERSHIP" current={limit} currency={currency} mayManage summary="" onSave={onSave} onClear={onClear} />
        </div>
      )}
    </Divided>
  );
}

function CategoriesRow({ marketId, codes, mayManage, onSave }: { marketId: string; codes: readonly string[] | null; mayManage: boolean; onSave: (codes: string[] | null) => Promise<boolean> }): ReactElement {
  const { t } = useT();
  const [text, setText] = useState((codes ?? []).join(', '));
  const parsed = text.split(',').map((c) => c.trim()).filter((c) => c !== '');
  return (
    <div>
      <Text role="caption" tone="secondary">{marketId}</Text>
      <FormRow
        columns="minmax(0, 480px)"
        action={mayManage ? <Button variant="secondary" onClick={() => void onSave(parsed.length === 0 ? null : parsed)}>{t(parsed.length === 0 ? 'pcc.categories.clear' : 'pcc.limit.save')}</Button> : undefined}
      >
        <Field label={t('pcc.categories.codes')} htmlFor={`pcc-cat-${marketId}`} hint={t('pcc.categories.codes.hint')}>
          <TextInput id={`pcc-cat-${marketId}`} value={text} disabled={!mayManage} onChange={(e) => setText(e.target.value)} />
        </Field>
      </FormRow>
    </div>
  );
}

function NewFare({ currency, onSave }: { currency: string | undefined; onSave: (minor: string) => Promise<boolean> }): ReactElement | null {
  const { t, lang } = useT();
  const { v } = useVocab();
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState<bigint | undefined>(undefined);
  if (currency === undefined) return null;
  if (!open) return <Button variant="ghost" onClick={() => setOpen(true)}>{t('pcc.quick.newFare')}</Button>;
  return (
    <FormRow
      action={
        <Button variant="secondary" disabled={value === undefined || value <= 0n} onClick={() => void onSave((value as bigint).toString()).then((ok) => { if (ok) { setOpen(false); setValue(undefined); } })}>{t('pcc.limit.save')}</Button>
      }
    >
      <MoneyInput id="pcc-new-fare" label={t('pcc.quick.fare')} currency={currency} locale={lang} valueMinor={value} onChangeMinor={setValue} issueText={(issue) => v('format.money_input_issue', issue)} />
    </FormRow>
  );
}

function NewZone({ markets, currency, onCreate }: { markets: readonly string[]; currency: string | undefined; onCreate: (input: { marketId: string; label: string; categoryCode: string; destination: { latitude: number; longitude: number }; destinationAddress?: string; fixedMinor: string; currency: string }) => Promise<boolean> }): ReactElement | null {
  const { t, lang } = useT();
  const { v } = useVocab();
  const [marketId, setMarketId] = useState(markets[0] ?? '');
  const [label, setLabel] = useState('');
  const [category, setCategory] = useState('');
  const [lat, setLat] = useState('');
  const [lng, setLng] = useState('');
  const [address, setAddress] = useState('');
  const [fare, setFare] = useState<bigint | undefined>(undefined);
  if (currency === undefined || markets.length === 0) return null;
  const latitude = Number(lat);
  const longitude = Number(lng);
  const ready = label.trim() !== '' && category.trim() !== '' && lat.trim() !== '' && lng.trim() !== '' && Number.isFinite(latitude) && Number.isFinite(longitude) && fare !== undefined && fare > 0n;
  return (
    <div>
      <FormGrid>
          <Field label={t('pob.field.market')} htmlFor="pcc-zone-market"><Select id="pcc-zone-market" value={marketId} onChange={(e) => setMarketId(e.target.value)}>{markets.map((m) => <option key={m} value={m}>{m}</option>)}</Select></Field>
          <Field label={t('pcc.col.name')} htmlFor="pcc-zone-label"><TextInput id="pcc-zone-label" value={label} maxLength={80} onChange={(e) => setLabel(e.target.value)} /></Field>
          <Field label={t('pcc.quick.category')} htmlFor="pcc-zone-cat"><TextInput id="pcc-zone-cat" value={category} maxLength={32} onChange={(e) => setCategory(e.target.value)} /></Field>
          <Field label={t('pob.field.latitude')} htmlFor="pcc-zone-lat"><TextInput id="pcc-zone-lat" className="tnum" inputMode="decimal" value={lat} onChange={(e) => setLat(e.target.value)} /></Field>
          <Field label={t('pob.field.longitude')} htmlFor="pcc-zone-lng"><TextInput id="pcc-zone-lng" className="tnum" inputMode="decimal" value={lng} onChange={(e) => setLng(e.target.value)} /></Field>
          <Field label={t('pcc.quick.address')} htmlFor="pcc-zone-addr"><TextInput id="pcc-zone-addr" value={address} maxLength={200} onChange={(e) => setAddress(e.target.value)} /></Field>
          <MoneyInput id="pcc-zone-fare" label={t('pcc.quick.fare')} currency={currency} locale={lang} valueMinor={fare} onChangeMinor={setFare} issueText={(issue) => v('format.money_input_issue', issue)} />
      </FormGrid>
      <Button variant="secondary" disabled={!ready} onClick={() => void onCreate({ marketId, label: label.trim(), categoryCode: category.trim(), destination: { latitude, longitude }, ...(address.trim() !== '' ? { destinationAddress: address.trim() } : {}), fixedMinor: (fare as bigint).toString(), currency }).then((ok) => { if (ok) { setLabel(''); setLat(''); setLng(''); setAddress(''); setFare(undefined); } })}>{t('pcc.quick.new')}</Button>
    </div>
  );
}
